/**
 * Stage 4: Trip Requirements (Age Verification + Special Needs)
 *
 * Honor system age verification for experiences with age requirements.
 * SCRUM-1292 (AC-08): When experience has participant types configured,
 * collects individual participant ages instead of honor-system checkbox.
 *
 * Optional dietary restrictions and questions for guide.
 *
 * Location: src/components/booking/flow/stages/Stage4Age.jsx
 * Epic: MVP 1.4 - Customer Booking Flow
 *
 * Features:
 * - Clear age requirement display
 * - Honor system checkbox (required if minimumAge > 0 and no participant types)
 * - Individual age inputs per participant (when participant types configured)
 * - Dietary restrictions (optional)
 * - Questions for guide (optional)
 * - Navigation buttons
 */

import { useState } from 'react';

/**
 * Stage4Age Component
 *
 * @param {Object} props
 * @param {Object} props.bookingData - Current booking form data
 * @param {number} props.minimumAge - Minimum age required for this experience (0 = no requirement)
 * @param {Function} props.onNext - Handler for proceeding to next stage
 * @param {Function} props.onBack - Handler for going back
 * @param {Function} props.trackProgressAction - Handler for tracking progress
 * @param {Array} [props.participantTypes] - Participant types configured for this experience
 * @returns {JSX.Element}
 */
export default function Stage4Age({ bookingData, minimumAge = 0, onNext, onBack, trackProgressAction, participantTypes }) {

  const hasParticipantTypes = participantTypes && participantTypes.length > 0;
  const partySize = bookingData.partySize || bookingData.guests || 1;

  const [formData, setFormData] = useState({
    ageVerified: bookingData.ageVerified || false,
    dietaryRestrictions: bookingData.dietaryRestrictions || '',
    questionsForGuide: bookingData.questionsForGuide || '',
    participantAges: bookingData.participantAges || Array(partySize).fill(''),
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
    trackProgressAction(4, 'field_completed');
  };

  const handleAgeChange = (index, value) => {
    setFormData(prev => {
      const ages = [...prev.participantAges];
      ages[index] = value;
      return { ...prev, participantAges: ages };
    });
    setErrors(prev => ({ ...prev, participantAges: null }));
    trackProgressAction(4, 'field_completed');
  };

  const validateForm = () => {
    const newErrors = {};

    if (hasParticipantTypes) {
      // AC-08: Validate all participant ages are filled
      const allFilled = formData.participantAges.every(age => age !== '' && age !== undefined);
      if (!allFilled) {
        newErrors.participantAges = 'Please enter an age for every participant';
      } else {
        const allValid = formData.participantAges.every(age => {
          const num = parseInt(age, 10);
          return !isNaN(num) && num >= 0 && num <= 120;
        });
        if (!allValid) {
          newErrors.participantAges = 'All ages must be between 0 and 120';
        }
      }
    } else {
      // Original honor-system validation
      if (minimumAge > 0 && !formData.ageVerified) {
        newErrors.ageVerified = 'You must confirm all participants meet the age requirement';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (  _e) => {
    _e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const nextData = {
      dietaryRestrictions: formData.dietaryRestrictions,
      questionsForGuide: formData.questionsForGuide,
      minimumAge,
    };

    if (hasParticipantTypes) {
      nextData.participantAges = formData.participantAges.map(a => parseInt(a, 10));
      nextData.ageVerified = true; // Implicit verification through age entry
    } else {
      nextData.ageVerified = formData.ageVerified;
    }

    onNext(nextData);
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Trip Requirements</h2>
          <p className="text-gray-600 mt-1">Help your guide prepare for your adventure</p>
        </div>

        {/* AC-08: Individual age inputs when participant types are configured */}
        {hasParticipantTypes && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="participant-ages-section">
            <p className="font-medium text-blue-900 mb-3">
              This experience has age-based pricing. Please enter each participant&apos;s age.
            </p>
            <div className="space-y-3">
              {Array.from({ length: partySize }, (_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <label className="text-sm text-gray-700 min-w-[120px]">
                    Participant {i + 1}
                  </label>
                  <input
                    type="number"
                    value={formData.participantAges[i] || ''}
                    onChange={(e) => handleAgeChange(i, e.target.value)}
                    min="0"
                    max="120"
                    placeholder="Age"
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    data-testid={`participant-age-${i}`}
                  />
                  <span className="text-sm text-gray-500">years</span>
                </div>
              ))}
            </div>
            {errors.participantAges && (
              <p className="text-red-500 text-sm mt-2">{errors.participantAges}</p>
            )}
          </div>
        )}

        {/* Age Verification — honor-system checkbox (only when no participant types) */}
        {!hasParticipantTypes && minimumAge > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="font-medium text-amber-900 mb-3">
              ⚠️ This experience requires all participants to be {minimumAge}+ years old
            </p>
            <label
              className={`flex items-start gap-3 p-3 border-2 rounded-lg cursor-pointer ${
                formData.ageVerified
                  ? 'border-emerald-500 bg-emerald-50'
                  : 'border-gray-300 bg-white'
              }`}
            >
              <input
                type="checkbox"
                checked={formData.ageVerified}
                onChange={(e) => handleInputChange('ageVerified', e.target.checked)}
                className="w-5 h-5 mt-0.5 text-emerald-500 rounded"
              />
              <span className="text-sm">
                I confirm all {partySize} participants meet the age requirement
              </span>
            </label>
            {errors.ageVerified && (
              <p className="text-red-500 text-sm mt-2">{errors.ageVerified}</p>
            )}
          </div>
        )}

        {/* Special Requirements (Optional) */}
        <div className="space-y-4">
          {/* Dietary Restrictions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dietary Restrictions (Optional)
            </label>
            <select
              value={formData.dietaryRestrictions}
              onChange={(e) => handleInputChange('dietaryRestrictions', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">None</option>
              <option value="vegetarian">Vegetarian</option>
              <option value="vegan">Vegan</option>
              <option value="gluten-free">Gluten-Free</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Questions for Guide */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Questions for Your Guide (Optional)
            </label>
            <textarea
              value={formData.questionsForGuide}
              onChange={(e) => handleInputChange('questionsForGuide', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 resize-none"
              rows={3}
              placeholder="Anything you'd like to ask before your trip..."
            />
          </div>
        </div>

        {/* Navigation Buttons */}
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={onBack}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Back
          </button>
          <button
            type="submit"
            className="flex-1 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}
